import { useState } from "react";
import { format, subHours, subDays, subWeeks } from "date-fns";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DateRangeShortcut = "1h" | "3h" | "12h" | "1d" | "1w" | "custom";

export interface DateRange {
  from?: Date;
  to?: Date;
}

export function getShortcutRange(shortcut: DateRangeShortcut): DateRange {
  if (shortcut === "custom") return {};
  const now = new Date();
  switch (shortcut) {
    case "1h":
      return { from: subHours(now, 1), to: now };
    case "3h":
      return { from: subHours(now, 3), to: now };
    case "12h":
      return { from: subHours(now, 12), to: now };
    case "1d":
      return { from: subDays(now, 1), to: now };
    case "1w":
    default:
      return { from: subWeeks(now, 1), to: now };
  }
}

interface DateRangeFilterProps {
  shortcut: DateRangeShortcut;
  customRange: DateRange;
  onShortcutChange: (shortcut: DateRangeShortcut, range: DateRange) => void;
  onCustomRangeChange: (range: DateRange) => void;
}

const OPTIONS: { label: string; value: DateRangeShortcut; description?: string }[] = [
  { label: "1h", value: "1h", description: "1 hour" },
  { label: "3h", value: "3h", description: "3 hours" },
  { label: "12h", value: "12h", description: "12 hours" },
  { label: "1d", value: "1d", description: "1 day" },
  { label: "1w", value: "1w", description: "1 week" },
  { label: "Custom", value: "custom", description: "Custom" },
];

function applyTime(date: Date, timeStr: string): Date {
  const [h, m, s] = timeStr.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
  return d;
}

export function DateRangeFilter({
  shortcut,
  customRange,
  onShortcutChange,
  onCustomRangeChange,
}: DateRangeFilterProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [draft, setDraft] = useState<DayPickerRange | undefined>(
    customRange.from || customRange.to ? { from: customRange.from, to: customRange.to } : undefined
  );
  const [startTime, setStartTime] = useState(() =>
    customRange.from ? format(customRange.from, "HH:mm:ss") : "00:00:00"
  );
  const [endTime, setEndTime] = useState(() =>
    customRange.to ? format(customRange.to, "HH:mm:ss") : "23:59:59"
  );
  const [timeError, setTimeError] = useState<string | null>(null);

  const isValidTime = (val: string) => /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(val);

  const handleCalendarSelect = (range: DayPickerRange | undefined) => {
    setDraft(range);
    // Auto-fill default times when calendar selection changes
    if (range?.from) setStartTime("00:00:00");
    if (range?.to) setEndTime("23:59:59");
  };

  const handleStartTimeChange = (val: string) => {
    setStartTime(val);
    if (draft?.from && isValidTime(val)) {
      setDraft((prev) => ({ from: applyTime(draft.from!, val), to: prev?.to }));
    }
  };

  const handleEndTimeChange = (val: string) => {
    setEndTime(val);
    if (draft?.to && isValidTime(val)) {
      setDraft((prev) => ({ from: prev?.from, to: applyTime(draft.to!, val) }));
    }
  };

  const handleShortcutClick = (val: DateRangeShortcut) => {
    if (val === "custom") {
      const seed = customRange.from || customRange.to
        ? { from: customRange.from, to: customRange.to }
        : undefined;
      setDraft(seed);
      setStartTime(customRange.from ? format(customRange.from, "HH:mm:ss") : "00:00:00");
      setEndTime(customRange.to ? format(customRange.to, "HH:mm:ss") : "23:59:59");
      setIsCustomOpen((prev) => !prev);
      return;
    }
    setIsCustomOpen(false);
    if (val === shortcut) return;
    onShortcutChange(val, getShortcutRange(val));
  };

  const handleApply = () => {
    setTimeError(null);
    if (!isValidTime(startTime)) {
      setTimeError("Start time is invalid. Use HH:mm:ss (24h).");
      return;
    }
    if (!isValidTime(endTime)) {
      setTimeError("End time is invalid. Use HH:mm:ss (24h).");
      return;
    }
    const from = draft?.from ? applyTime(draft.from, startTime) : undefined;
    const to = draft?.to ? applyTime(draft.to, endTime) : undefined;
    if (from && to && from >= to) {
      setTimeError("Start datetime must be before end datetime.");
      return;
    }
    const range: DateRange = { from, to };
    onCustomRangeChange(range);
    onShortcutChange("custom", range);
    setIsCustomOpen(false);
  };

  const label =
    shortcut === "custom" && customRange.from && customRange.to
      ? `${format(customRange.from, "MMM d")} – ${format(customRange.to, "MMM d")}`
      : "Custom";

  const fieldClass =
    "min-w-0 h-9 w-full rounded border border-border bg-transparent px-2 text-sm text-secondary placeholder:text-muted/30 focus:outline-none focus:ring-1 focus:ring-brand-cyan/50 focus:border-brand-cyan/40 transition-colors disabled:opacity-40";

  return (
    <div className="flex items-center rounded-md border border-border bg-transparent p-1 gap-0.5">
      {OPTIONS.filter((o) => o.value !== "custom").map((opt) => (
        <button
          key={opt.value}
          onClick={() => handleShortcutClick(opt.value)}
          title={opt.description}
          className={cn(
            "px-3 py-1.5 flex items-center justify-center text-[13px] font-medium rounded-[4px] transition-colors",
            shortcut === opt.value
              ? "bg-brand-cyan/10 text-brand-cyan"
              : "text-muted hover:text-primary hover:bg-surface-elevated"
          )}
        >
          {opt.label}
        </button>
      ))}

      <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <PopoverTrigger asChild>
          <button
            onClick={() => handleShortcutClick("custom")}
            title="Custom"
            className={cn(
              "px-3 py-1.5 flex items-center justify-center text-[13px] font-medium rounded-[4px] transition-colors",
              shortcut === "custom"
                ? "bg-brand-cyan/10 text-brand-cyan"
                : "text-muted hover:text-primary hover:bg-surface-elevated"
            )}
          >
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 max-w-min" align="end">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={handleCalendarSelect}
            numberOfMonths={2}
            initialFocus
          />
          {/* Date/time inputs — single row, width-capped to calendar */}
          <div className="border-t border-border px-4 py-4 flex gap-3 w-full max-w-full overflow-hidden">
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-muted tracking-wide truncate">Start date</span>
              <input
                type="text"
                readOnly
                value={draft?.from ? format(draft.from, "yyyy-MM-dd") : ""}
                placeholder="yyyy-MM-dd"
                className={fieldClass}
              />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <span className="text-[13px] font-medium text-muted tracking-wide truncate">Start time</span>
              <input
                type="text"
                value={startTime}
                onChange={(e) => { setTimeError(null); handleStartTimeChange(e.target.value); }}
                disabled={!draft?.from}
                placeholder="HH:mm:ss"
                className={cn(fieldClass, timeError?.startsWith("Start") && "border-rose-400 focus:ring-rose-400/50")}
              />
            </div>
            <div className="flex flex-col justify-end pb-1.5 px-1">
              <div className="w-px h-5 bg-border/50" />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-muted tracking-wide truncate">End date</span>
              <input
                type="text"
                readOnly
                value={draft?.to ? format(draft.to, "yyyy-MM-dd") : ""}
                placeholder="yyyy-MM-dd"
                className={fieldClass}
              />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <span className="text-[13px] font-medium text-muted tracking-wide truncate">End time</span>
              <input
                type="text"
                value={endTime}
                onChange={(e) => { setTimeError(null); handleEndTimeChange(e.target.value); }}
                disabled={!draft?.to}
                placeholder="HH:mm:ss"
                className={cn(fieldClass, timeError?.startsWith("End") && "border-rose-400 focus:ring-rose-400/50")}
              />
            </div>
          </div>
          {timeError && (
            <p className="px-4 pb-3 text-xs text-rose-500">{timeError}</p>
          )}
          <div className="flex justify-end gap-3 px-4 pb-4">
            <button
              onClick={() => setIsCustomOpen(false)}
              className="py-1.5 px-3 rounded-md hover:bg-surface-elevated text-muted/80 text-xs font-medium transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!draft?.from || !draft?.to}
              className="flex items-center gap-1.5 py-1.5 px-3 rounded-md bg-brand-cyan hover:bg-brand-cyan/90 border border-brand-cyan text-white text-xs font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="h-3.5 w-3.5" />
              Apply
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
